import React, { useState, useEffect, useRef } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Eye, 
  EyeOff, 
  Delete
} from 'lucide-react';
import './NFCTimeTracking.css';

interface NFCResponse {
  type: 'CLOCK_IN' | 'CLOCK_OUT' | 'NFC_NOT_FOUND' | 'USER_INACTIVE' | 'SYSTEM_ERROR' | 'SYSTEM_MESSAGE' | 'SHOW_WORK_TIME';
  user?: { firstName: string; lastName: string };
  duration?: {
    grossMinutes: number;
    netMinutes: number;
    breakMinutes: number;
    hours: number;
    minutes: number;
  };
  currentWorkTime?: {
    startTime: string;
    elapsedMinutes: number;
    elapsedHours: number;
    elapsedMins: number;
  };
  message?: string;
  error?: string;
  nfcId?: string;
}

export const NFCTimeTracking: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [nfcId, setNfcId] = useState('');
  const [response, setResponse] = useState<NFCResponse | null>(null);
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationStep, setRegistrationStep] = useState<'email' | 'password'>('email');
  const [registrationData, setRegistrationData] = useState({
    email: '',
    password: '',
    nfcId: ''
  });
  const [loading, setLoading] = useState(false);
  const nfcInputRef = useRef<HTMLInputElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [activeField, setActiveField] = useState<'email' | 'password' | null>(null);
  const [keyboardLayout, setKeyboardLayout] = useState<'letters' | 'bigLetters' | 'numbers' | 'symbols'>('letters');
  const [showPassword, setShowPassword] = useState(false);
  const [secondsProgress, setSecondsProgress] = useState(0);
  const [showWorkTimeMode, setShowWorkTimeMode] = useState(false);
  
  // NFC Input Buffer für Kartenleser
  const nfcBufferRef = useRef<string>('');
  const nfcBufferTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [, setDebugInput] = useState<string>('');
  const [, setLastKeyPressed] = useState<string>('');

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      // Calculate seconds progress (0-60 seconds as 0-100%)
      const seconds = now.getSeconds();
      setSecondsProgress((seconds / 60) * 100);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Globaler Keyboard-Event-Listener für Kartenleser
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Debug: Alle Tasteneingaben anzeigen
      setLastKeyPressed(event.key);
      
      // Nur verarbeiten wenn wir nicht im Registrierungsmodus sind
      // UND nicht während eine Response angezeigt wird (außer im showWorkTimeMode)
      if (showRegistration || (response && !showWorkTimeMode)) return;

      // Verhindere normale Tastatur-Events
      event.preventDefault();
      event.stopPropagation();

      const key = event.key;

      // Enter-Taste: NFC-ID verarbeiten
      if (key === 'Enter') {
        if (nfcBufferRef.current.trim()) {
          const nfcIdValue = nfcBufferRef.current.trim();
          setDebugInput(nfcIdValue);
          
          // Buffer sofort leeren
          nfcBufferRef.current = '';
          
          // Clear timeout
          if (nfcBufferTimeoutRef.current) {
            clearTimeout(nfcBufferTimeoutRef.current);
          }
          
          // Direkt handleNfcSubmit aufrufen mit der NFC-ID
          handleNfcSubmit(new Event('submit') as any, nfcIdValue);
        }
        return;
      }

      // Escape-Taste: Buffer leeren
      if (key === 'Escape') {
        nfcBufferRef.current = '';
        setDebugInput('');
        if (nfcBufferTimeoutRef.current) {
          clearTimeout(nfcBufferTimeoutRef.current);
        }
        return;
      }

      // Backspace: Letztes Zeichen entfernen
      if (key === 'Backspace') {
        nfcBufferRef.current = nfcBufferRef.current.slice(0, -1);
        setDebugInput(nfcBufferRef.current);
        return;
      }

      // Normale Zeichen hinzufügen (Buchstaben, Zahlen, etc.)
      if (key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
        nfcBufferRef.current += key;
        setDebugInput(nfcBufferRef.current);
      }
    };

    // Event-Listener hinzufügen
    document.addEventListener('keydown', handleGlobalKeyDown, true);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
      if (nfcBufferTimeoutRef.current) {
        clearTimeout(nfcBufferTimeoutRef.current);
      }
    };
  }, [showRegistration, response, showWorkTimeMode]);

  // Focus NFC input on mount (nur für visuellen Effekt, nicht für Funktionalität)
  useEffect(() => {
    if (nfcInputRef.current && !showRegistration && !response) {
      nfcInputRef.current.focus();
    }
  }, [showRegistration, response]);

  // Focus email input when registration form is shown
  useEffect(() => {
    if (showRegistration) {
      setTimeout(() => {
        setActiveField(registrationStep);
        setShowKeyboard(true);
        setKeyboardLayout('letters');
      }, 200);
    } else {
      setShowKeyboard(false);
      setActiveField(null);
    }
  }, [showRegistration, registrationStep]);

  // Auto-reset to main screen after 6 seconds
  useEffect(() => {
    if (response && (response.type === 'CLOCK_IN' || response.type === 'CLOCK_OUT' || response.type === 'SYSTEM_MESSAGE')) {
      timeoutRef.current = setTimeout(() => {
        resetToMainScreen();
      }, 6000);
    }
    
    // Auto-reset for SHOW_WORK_TIME after 5 seconds
    if (response && response.type === 'SHOW_WORK_TIME') {
      timeoutRef.current = setTimeout(() => {
        resetToMainScreen();
      }, 5000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [response]);

  const resetToMainScreen = () => {
    setResponse(null);
    setShowRegistration(false);
    setRegistrationStep('email');
    setNfcId('');
    setRegistrationData({ email: '', password: '', nfcId: '' });
    setShowWorkTimeMode(false);
    
    // NFC Buffer und Debug-Variablen leeren
    nfcBufferRef.current = '';
    setDebugInput('');
    setLastKeyPressed('');
    if (nfcBufferTimeoutRef.current) {
      clearTimeout(nfcBufferTimeoutRef.current);
    }
    
    if (nfcInputRef.current) {
      nfcInputRef.current.focus();
    }
  };

  const handleNfcSubmit = async (e: React.FormEvent, directNfcId?: string) => {
    e.preventDefault();
    
    const nfcIdToUse = directNfcId || nfcId;
    if (!nfcIdToUse.trim()) return;

    setLoading(true);

    try {
      // If in show work time mode, use different endpoint
      const endpoint = showWorkTimeMode ? '/api/nfc/show-work-time' : '/api/nfc/time-tracking';
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nfcId: nfcIdToUse.trim() })
      });

      const data = await response.json();
      setResponse(data);

      if (data.type === 'NFC_NOT_FOUND') {
        setRegistrationData(prev => ({ ...prev, nfcId: data.nfcId }));
        setRegistrationStep('email');
        setShowRegistration(true);
      }

    } catch (error) {
      console.error('NFC tracking error:', error);
      setResponse({
        type: 'SYSTEM_ERROR',
        error: 'Verbindungsfehler'
      });
    } finally {
      setLoading(false);
      setNfcId('');
      
      // NFC Buffer und Debug-Variablen leeren
      nfcBufferRef.current = '';
      setDebugInput('');
      if (nfcBufferTimeoutRef.current) {
        clearTimeout(nfcBufferTimeoutRef.current);
      }
    }
  };

  const handleEmailStep = () => {
    if (!registrationData.email.trim()) {
      setResponse({
        type: 'SYSTEM_ERROR',
        error: 'Bitte geben Sie eine gültige E-Mail ein.'
      });
      return;
    }
    setRegistrationStep('password');
    setActiveField('password');
    setKeyboardLayout('letters');
  };

  const handleRegistration = async () => {
    if (!registrationData.email || !registrationData.password) {
      setResponse({
        type: 'SYSTEM_ERROR',
        error: 'Bitte geben Sie eine gültige E-Mail und ein Passwort ein.'
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/nfc/register-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData)
      });

      const data = await response.json();

      if (response.ok) {
        setResponse({
          type: 'SYSTEM_MESSAGE',
          message: 'Die NFC-Karte wurde erfolgreich registriert.'
        });
        setShowRegistration(false);

        // Automatische Rückkehr zur Hauptansicht nach 5 Sekunden
        timeoutRef.current = setTimeout(() => {
          resetToMainScreen();
        }, 4000);
      } else {
        // Zeige die Fehlermeldung und lasse das Formular offen
        setResponse({
          type: 'SYSTEM_ERROR',
          error: data.error || 'Registrierung fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.'
        });
      }

    } catch (error) {
      console.error('Registration error:', error);
      setResponse({
        type: 'SYSTEM_ERROR',
        error: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const handleKeyPress = (key: string) => {
    if (!activeField) return;

    const currentField = activeField === 'email' ? 'email' : 'password';
    const currentValue = registrationData[currentField];

    if (key === 'Backspace') {
      setRegistrationData(prev => ({
        ...prev,
        [currentField]: currentValue.slice(0, -1)
      }));
    } else if (key === 'Space') {
      setRegistrationData(prev => ({
        ...prev,
        [currentField]: currentValue + ' '
      }));
    } else if (key === 'Enter') {
      if (activeField === 'email' && registrationData.email) {
        handleEmailStep();
      } else if (activeField === 'password' && registrationData.password) {
        // Submit form
        handleRegistration();
      }
    } else if (key.length === 1) {
      setRegistrationData(prev => ({
        ...prev,
        [currentField]: currentValue + key
      }));
    }
  };

  const switchKeyboardLayout = (layout: 'letters' | 'bigLetters' | 'numbers' | 'symbols') => {
    setKeyboardLayout(layout);
  };

  const handleClockClick = () => {
    // Reset any existing response
    setResponse(null);
    setShowWorkTimeMode(true);
    
    // Auto-reset after 30 seconds if no NFC scan
    const timeoutId = setTimeout(() => {
      setShowWorkTimeMode(false);
    }, 30000);

    return () => clearTimeout(timeoutId);
  };

  const KeyboardComponent = () => {
    const letterKeys = [
      ['q', 'w', 'e', 'r', 't', 'z', 'u', 'i', 'o', 'p', 'ü'],
      ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä'],
      ['y', 'x', 'c', 'v', 'b', 'n', 'm', '.','@']
    ];

    const bigLetterKeys = [
      ['Q', 'W', 'E', 'R', 'T', 'Z', 'U', 'I', 'O', 'P', 'Ü'],
      ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ö', 'Ä'],
      ['Y', 'X', 'C', 'V', 'B', 'N', 'M', '.', '@']
    ];

    const numberKeys = [
      ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
      ['@', '#', '$', '%', '&', '*', '(', ')', '-', '_'],
      ['+', '=', '[', ']', '{', '}', '\\', '|', ';', ':']
    ];

    const symbolKeys = [
      ['!', '"', '§', '$', '%', '&', '/', '(', ')', '='],
      ['?', '`', '´', '^', '<', '>', '|', '~'],
      ['{', '}', '[', ']', '\\', '@', '€']
    ];

    const currentKeys = keyboardLayout === 'letters' ? letterKeys :
      keyboardLayout === 'bigLetters' ? bigLetterKeys : keyboardLayout === 'numbers' ? numberKeys : symbolKeys;

    return (
      <div className="virtual-keyboard">
        <div className="keyboard-header">
          <div className="field-indicator">
            {activeField === 'email' ? 'E-Mail eingeben' : 'Passwort eingeben'}
          </div>
          <div className="keyboard-switcher">
            <button 
              className={`kb-switch ${keyboardLayout === 'letters' ? 'active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              onClick={() => switchKeyboardLayout('letters')}
            >
              abc
            </button>
            <button 
              className={`kb-switch ${keyboardLayout === 'bigLetters' ? 'active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              onClick={() => switchKeyboardLayout('bigLetters')}
            >
              ABC
            </button>
            <button 
              className={`kb-switch ${keyboardLayout === 'numbers' ? 'active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              onClick={() => switchKeyboardLayout('numbers')}
            >
              123
            </button>
            <button 
              className={`kb-switch ${keyboardLayout === 'symbols' ? 'active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              onClick={() => switchKeyboardLayout('symbols')}
            >
              !@#
            </button>
            <button 
              className="keyboard-key special" 
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              onClick={() => handleKeyPress('Backspace')}
            >
              <Delete size={28} />
            </button>
          </div>
        </div>
        
        <div className="keyboard-rows">
          {currentKeys.map((row, rowIndex) => (
            <div key={rowIndex} className="keyboard-row">
              {row.map((key) => (
                <button
                  key={key}
                  className={`keyboard-key ${key === '@' ? 'atgreen' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.preventDefault()}
                  onClick={() => handleKeyPress(key)}
                >
                  {key}
                </button>
              ))}
            </div>
          ))}
          
          <div className="keyboard-row special-row">
        
            <button 
              className="keyboard-key space" 
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              onClick={() => handleKeyPress('Space')}
            >
              Leerzeichen
            </button>
          
         
          </div>
        </div>
      </div>
    );
  };

  // Show registration form
  if (showRegistration) {
    return (
      <div className="nfc-container registration-mode">
        <div className="registration-content">
          <div className="registration-card">
            <div className="registration-header">
              <h2>Neuer Chip erkannt <br />Jetzt Ihnen zuordnen</h2>
            </div>
            <p className="nfc-id">ID: {registrationData.nfcId}</p>
            
            {/* Progress indicator */}
            <div className="registration-progress">
              <div className={`progress-step ${registrationStep === 'email' ? 'active' : registrationStep === 'password' ? 'completed' : ''}`}>
                <span className="step-number">1</span>
                <span className="step-label">E-Mail</span>
              </div>
              <div className={`progress-step ${registrationStep === 'password' ? 'active' : ''}`}>
                <span className="step-number">2</span>
                <span className="step-label">Passwort</span>
              </div>
            </div>
            
            {response?.type === 'SYSTEM_ERROR' && (
              <div className="error-message">
                <AlertCircle size={20} />
                <p>{response.error}</p>
              </div>
            )}

            <div className="registration-form">
              {registrationStep === 'email' && (
                <>
                  <div className="form-group">
                    <label>E-Mail:</label>
                    <input
                      ref={emailInputRef}
                      type="email"
                      value={registrationData.email}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, email: e.target.value }))}
                      onFocus={() => {
                        setActiveField('email');
                        setShowKeyboard(true);
                        setKeyboardLayout('letters');
                      }}
                      placeholder="ihre.email@mail.de"
                      readOnly
                      className={activeField === 'email' ? 'active-input' : ''}
                    />
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn primary" 
                      disabled={!registrationData.email.trim()}
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={handleEmailStep}
                    >
                      Weiter
                    </button>
                    <button 
                      type="button" 
                      className="btn secondary abbrechen-btn" 
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={resetToMainScreen}
                    >
                      Abbrechen
                    </button>
                  </div>
                </>
              )}

              {registrationStep === 'password' && (
                <>
                  <div className="form-group">
                    <label>Passwort:</label>
                    <div className="password-input-container">
                      <input
                        ref={passwordInputRef}
                        type={showPassword ? "text" : "password"}
                        value={registrationData.password}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, password: e.target.value }))}
                        onFocus={() => {
                          setActiveField('password');
                          setShowKeyboard(true);
                          setKeyboardLayout('letters');
                        }}
                        placeholder="Ihr Passwort"
                        readOnly
                        className={activeField === 'password' ? 'active-input' : ''}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onMouseDown={(e) => e.preventDefault()}
                        onTouchStart={(e) => e.preventDefault()}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={28} /> : <Eye size={28} />}
                      </button>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn primary" 
                      disabled={loading || !registrationData.password.trim()}
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={handleRegistration}
                    >
                      {loading ? 'Registriere...' : 'Fertigstellen'}
                    </button>
                    <button 
                      type="button" 
                      className="btn secondary" 
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={() => setRegistrationStep('email')}
                    >
                      Zurück
                    </button>
                    <button 
                      type="button" 
                      className="btn secondary abbrechen-btn" 
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={resetToMainScreen}
                    >
                      Abbrechen
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {showKeyboard && <KeyboardComponent />}
        </div>
      </div>
    );
  }

  // Show response messages
  if (response) {
    if (response.type === 'CLOCK_IN') {
      return (
        <div className="nfc-container clock-in-mode">
          <div className="status-display clock-in">
            <div className="status-icon">
              <CheckCircle size={64} />
            </div>
            <h1>Eingestempelt</h1>
            <h2>Willkommen, {response.user?.firstName}!</h2>
            <p className="time">{formatTime(currentTime)}</p>
            <p className="auto-return">Automatische Rückkehr in wenigen Sekunden...</p>
          </div>
        </div>
      );
    }

    if (response.type === 'CLOCK_OUT') {
      return (
        <div className="nfc-container clock-out-mode">
          <div className="status-display clock-out">
            <div className="status-icon">
              <XCircle size={64} />
            </div>
            <h1>Ausgestempelt</h1>
            <h2>Auf Wiedersehen, {response.user?.firstName}!</h2>
            
            {response.duration && (
              <div className="duration-info">
                <div className="duration-main">
                  <span className="label">Arbeitszeit: </span>
                  <span className="value">{formatDuration(response.duration.netMinutes)}</span>
                </div>
                
                {response.duration.breakMinutes > 0 && (
                  <div className="duration-detail">
                    <span>Brutto: {formatDuration(response.duration.grossMinutes)}</span>
                    <span>Pause: -{formatDuration(response.duration.breakMinutes)}</span>
                  </div>
                )}
              </div>
            )}
            
            <p className="time">{formatTime(currentTime)}</p>
            <p className="auto-return">Automatische Rückkehr in wenigen Sekunden...</p>
          </div>
        </div>
      );
    }

    if (response.type === 'USER_INACTIVE') {
      return (
        <div className="nfc-container error-mode">
          <div className="status-display error">
            <div className="status-icon">
              <AlertCircle size={64} />
            </div>
            <h1>Zugang verweigert</h1>
            <h2>Benutzer {response.user?.firstName} {response.user?.lastName} ist deaktiviert</h2>
            <p>Bitte wenden Sie sich an einen Administrator.</p>
            <button className="btn secondary" onClick={resetToMainScreen}>
              Zurück
            </button>
          </div>
        </div>
      );
    }

    if (response.error) {
      return (
        <div className="nfc-container error-mode">
          <div className="status-display error">
            <div className="status-icon">
              <AlertCircle size={64} />
            </div>
            <h1>Fehler</h1>
            <p>{response.error}</p>
            <button className="btn secondary" onClick={resetToMainScreen}>
              Zurück
            </button>
          </div>
        </div>
      );
    }

    // Zeige allgemeine Systemmeldungen
    if (response?.type === 'SYSTEM_MESSAGE') {
      return (
        <div className="nfc-container registration-mode">
          <div className="registration-card">
            <div className="success-icon">
              <CheckCircle size={48} />
            </div>
            <h1>Erfolg</h1>
            <p>{response.message}</p>
            <p className="auto-return">Automatische Rückkehr in wenigen Sekunden...</p>
          </div>
        </div>
      );
    }
  }

  // Show work time display
  if (response && response.type === 'SHOW_WORK_TIME') {
    return (
      <div className="nfc-container show-work-time-mode">
        <div className="status-display show-work-time">
          <div className="status-icon">
            <CheckCircle size={64} />
          </div>
          <h2>{response.user?.firstName} {response.user?.lastName}</h2>
          
          {response.currentWorkTime ? (
            <>
              <h1>Aktuelle Arbeitszeit</h1>
              <div className="work-time-info">
                <div className="work-time-main">
                  <p className="time-large">
                    {response.currentWorkTime.elapsedHours}h {response.currentWorkTime.elapsedMins}m
                  </p>
                  <small>seit {new Date(response.currentWorkTime.startTime).toLocaleTimeString('de-DE', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })} Uhr</small>
                </div>
              </div>
            </>
          ) : (
            <>
              <h1>Nicht eingestempelt</h1>
              <div className="work-time-info">
                <p className="not-clocked-in-message">Sie sind aktuell nicht eingestempelt</p>
              </div>
            </>
          )}
          
          <p className="time">{formatTime(currentTime)}</p>
          <p className="auto-return">Automatische Rückkehr in wenigen Sekunden...</p>
        </div>
      </div>
    );
  }

  // Main screen - clock display
  return (
    <div className="nfc-container main-mode">
      <div className="circular-progress" style={{ '--progress': `${secondsProgress}%` } as React.CSSProperties}>
      <div className="clock-display">
          <h1 className="app-name"><img src="/chronios.svg" alt="Chronios" /></h1>
          
          {/* Debug-Anzeige für Tastatureingaben 
          <div className="debug-display">
            <div className="debug-info">
              <strong>Letzte Taste:</strong> {lastKeyPressed || 'Keine'}
            </div>
            <div className="debug-info">
              <strong>Buffer:</strong> {debugInput || 'Leer'}
            </div>
            <div className="debug-info">
              <strong>NFC ID:</strong> {nfcId || 'Keine'}
            </div>
          </div>
          */}
          <div 
            className={`current-time ${showWorkTimeMode ? 'show-work-time-active' : ''}`}
            onClick={handleClockClick}
            style={{ cursor: 'pointer' }}
            title="Klicken für Arbeitszeitanzeige"
          >
            <div className="time">{formatTime(currentTime)}</div>
            <div className="date">{formatDate(currentTime)}</div>
            {showWorkTimeMode && (
              <div className="work-time-hint">
                💼 Chip für Arbeitszeit scannen...
              </div>
            )}
          </div>
          
        {/*   <div className="instruction">
            <div className="instruction-icon">
              <Nfc size={32} />
            </div>
            <p>Halten Sie Ihre NFC-Chip an das <br /> Lesegerät für das Ein-/Ausstempeln</p>
          </div> */}

          {/* Hidden NFC input field */}
          <form onSubmit={handleNfcSubmit} className="nfc-form">
            <input
              ref={nfcInputRef}
              type="text"
              value={nfcId || nfcBufferRef.current}
              onChange={(e) => setNfcId(e.target.value)}
              className="nfc-input"
              placeholder="NFC ID wird automatisch eingegeben..."
              autoComplete="off"
              tabIndex={-1}
              readOnly
            />
          </form>

          {loading && (
            <div className="loading-indicator">
              <div className="spinner"></div>
              <p>Verarbeite...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
